/**
 * Title row with optional icon and close button; wires to aria-labelledby via id.
 * Architectural Context: Pure UI molecule. When used as Dialog.Header, receives titleId and onClose from context.
 *
 * @param title - Dialog title; used for aria-labelledby when id is provided
 * @param icon - Optional leading icon (e.g. AlertCircle); wrapped with IconWrapper
 * @param iconProps - Optional IconWrapper props (size, color, etc.)
 * @param onClose - Handler for close button; supplied by Dialog context when omitted
 * @param showCloseButton - Whether to show the X button
 * @default showCloseButton true
 *
 * @example
 * <Dialog.Header title="Usage Limit Exceeded" icon={<AlertCircle />} showCloseButton />
 */

import React from 'react';
import { Button } from '../../atoms/Button';
import { IconWrapper } from '../../atoms/IconWrapper';
import type { IconWrapperProps } from '../../atoms/IconWrapper/IconWrapper.types';
import { Typography } from '../../atoms/Typography';
import type { TypographyVariant } from '../../atoms/Typography/Typography.types';
import { X } from 'lucide-react';
import { typographyToIconSize } from './Dialog.styles';

export interface DialogHeaderProps {
  /** Dialog title */
  title: string;
  /** Typography variant for the title; icon size is derived from this */
  titleVariant?: TypographyVariant;
  /** Raw icon element; wrapped with IconWrapper for consistent sizing */
  icon?: React.ReactNode;
  /** Props passed to IconWrapper (size, color, etc.) */
  iconProps?: Omit<IconWrapperProps, 'children'>;
  /** Handler for close button */
  onClose?: () => void;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Props passed to close button (aria-label, etc.) */
  closeButtonProps?: {
    'aria-label'?: string;
    [key: string]: unknown;
  };
  /** Class name for the container */
  className?: string;
  /** ID for the title */
  id?: string;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  titleVariant = 'h3',
  icon,
  iconProps,
  onClose,
  showCloseButton = true,
  closeButtonProps,
  className = '',
  id,
}) => {
  const iconSize = iconProps?.size ?? typographyToIconSize[titleVariant];
  return (
  <div className={`flex justify-between items-center gap-2 sm:gap-3 ${className}`}>
    <div className="flex items-center space-x-2">
      {icon && (
        <IconWrapper size={iconSize} color="inherit" className="mb-0.5" ariaHidden {...iconProps}>
          {icon}
        </IconWrapper>
      )}
      <Typography variant={titleVariant} id={id} className="break-words pretty-text">
        {title}
      </Typography>
    </div>
    {showCloseButton && onClose && (
      <Button
        type="icon"
        intent="muted"
        onClick={onClose}
        icon={<X/>}
        label={closeButtonProps?.['aria-label'] || 'Close'}
        className="flex-shrink-0"
        {...closeButtonProps}
      />
    )}
  </div>
  );
};
